import { Colors } from "@/enums/Colors";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { useResponsive } from "@/hooks/useResponsive";
import { customMuiTheme } from "@/utils/AppThemeStyle";
import { getValidFiles } from "@/utils/LazoFIleUtils";
import { Box, styled, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import DataRoomHomeSVG from "../../../assets/images/data-room/data-room-home-img.svg";
import CommonButton from "../utils/CommonButton";
import { DEFAULT_FILE_SIZE_UPLOAD_LIMIT, ROOMS_FILE_SIZE_UPLOAD_LIMIT } from "../utils/Constants";
import { PurpleFilesIcon } from "../utils/icons/PurpleFilesIcon";
import { SimpleStarIcon } from "../utils/icons/SimpleStarIcon";
import DataRoomDocumentCenterImportModal from "./DataRoomDocumentCenterImportModal";

type Props = {
  onFileUpload: (files: File[]) => void;
  hideSubtitle?: boolean;
  recommendImageRatio?: boolean;
  isImage?: boolean;
  disableMultiple?: boolean;
  disableImportDocCenter?: boolean;
};

function DataRoomFileDragDrop({
  onFileUpload,
  hideSubtitle,
  recommendImageRatio,
  isImage,
  disableMultiple,
  disableImportDocCenter,
}: Props) {
  const { t } = useTranslation("virtualDataRooms");
  const { isMobile, isTablet, isDesktop } = useResponsive();
  const isMobileOrTablet = isMobile || isTablet;
  let allowedFormats = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ];

  allowedFormats = isImage ? ["image/png", "image/jpeg"] : allowedFormats;

  function onFileDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();

    const fs = [];
    if (e.dataTransfer.items) {
      for (let i = 0; i < e.dataTransfer.items.length; i++) {
        const item = e.dataTransfer.items[i];
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file && allowedFormats.includes(file.type)) {
            fs.push(file);
          } else {
            NotificationService.error(t("fileUpload.dragDrop.errors.invalidFormat"));
          }
        }
      }
    } else {
      for (let i = 0; i < e.dataTransfer.files.length; i++) {
        const file = e.dataTransfer.files[i];
        if (allowedFormats.includes(file.type)) {
          fs.push(file);
        }
      }
    }

    if (fs.length > 0) {
      disableMultiple ? onFileUpload([fs[0]]) : onFileUpload(fs);
    } else {
      NotificationService.error(t("fileUpload.dragDrop.errors.invalidFormat"));
    }

    const filesToUpload = getValidFiles(fs, ROOMS_FILE_SIZE_UPLOAD_LIMIT);
    if (filesToUpload.length > 0) {
      onFileUpload(filesToUpload);
    }
  }

  function handleBrowseClick() {
    const i = document.createElement("input");
    i.type = "file";
    if (!disableMultiple) {
      i.multiple = true;
    }
    if (isImage) {
      i.accept = "image/png, image/jpeg";
    } else {
      i.accept = ".pdf, .doc, .docx, .xls, .xlsx";
    }

    i.addEventListener("change", () => {
      if (i.files.length > 0) {
        let fs = [];
        for (let j = 0; j < i.files.length; j++) {
          const file = i.files[j];
          if (isImage) {
            if (["image/png", "image/jpeg"].includes(file.type)) {
              fs.push(file);
            }
          } else {
            if (
              [
                "application/pdf",
                "application/msword",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                "application/vnd.ms-excel",
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              ].includes(file.type)
            ) {
              fs.push(file);
            } else {
              NotificationService.error(
                isImage ? t("fileUpload.dragDrop.errors.invalidImageFormat") : t("fileUpload.dragDrop.errors.invalidFormat")
              );
            }
          }
        }
        const filesToUpload = getValidFiles(fs, ROOMS_FILE_SIZE_UPLOAD_LIMIT);
        if (filesToUpload.length > 0) {
          onFileUpload(filesToUpload);
        }
      }
    });

    i.click();
  }

  const [openDocumentCenterImportModal, setOpenDocumentCenterImportModal] = useState<boolean>(false);

  const handleTouchStart = (e: React.TouchEvent) => {
    const target = e.currentTarget.querySelector(".inside-container") as HTMLElement;
    if (target) {
      target.style.backgroundColor = Colors.ACCENT_F1F3FF;
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const target = e.currentTarget.querySelector(".inside-container") as HTMLElement;
    if (target) {
      setTimeout(() => {
        target.style.backgroundColor = "transparent";
      }, 150);
    }
  };

  return (
    <>
      <FileUploadContainer
        onClick={handleBrowseClick}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onFileDrop}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <Box className="inside-container">
          <img
            src={DataRoomHomeSVG.src}
            style={{
              width: isMobile ? "80px" : isTablet ? "100px" : "128px",
              height: isMobile ? "80px" : isTablet ? "100px" : "128px",
            }}
          />
          <Box display="flex" flexDirection="row" gap="0.5rem" alignItems="center">
            <Typography
              sx={{
                fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
                fontFamily: customMuiTheme.typography.fontFamily,
                textAlign: "center",
                lineHeight: { xs: 1.2, sm: 1.3, md: 1.4 },
              }}
              fontWeight={customMuiTheme.typography.fontWeightBold}
            >
              {t(isMobile ? "fileUpload.dragDrop.titleMobile" : "fileUpload.dragDrop.title", {
                type: isImage ? t("fileUpload.dragDrop.titleImage") : t("fileUpload.dragDrop.titleFiles"),
              })}
            </Typography>
          </Box>
          {!hideSubtitle && (
            <>
              <Typography
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: ".25rem",
                  fontSize: { xs: "0.625rem", sm: "0.6875rem", md: "0.75rem" },
                  fontFamily: customMuiTheme.typography.fontFamily,
                  fontWeight: customMuiTheme.typography.fontWeightBold,
                  textAlign: "center",
                  flexWrap: "wrap",
                }}
              >
                {t("fileUpload.dragDrop.aiAnalysisInfo")} <SimpleStarIcon style={{ width: "16px", height: "16px", fill: "black" }} />
              </Typography>
            </>
          )}
          {!isImage && (
            <Typography
              sx={{
                fontSize: { xs: "0.625rem", sm: "0.6875rem", md: "0.75rem" },
                textAlign: "center",
                lineHeight: { xs: "14px", sm: "16px", md: "18px" },
              }}
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap="0.25rem"
            >
              {t("fileUpload.dragDrop.formatInfo")}
            </Typography>
          )}
          {recommendImageRatio && (
            <Typography
              sx={{
                color: Colors.LIGHT_GRAY_8A8E93,
                fontSize: { xs: "10px", sm: "11px", md: "12px" },
                textAlign: "center",
              }}
            >
              {t("fileUpload.dragDrop.recommendedRatio")}
            </Typography>
          )}
          <Typography
            sx={{
              fontSize: { xs: "0.625rem", sm: "0.6875rem", md: "0.75rem" },
              color: Colors.LIGHT_GRAY_8A8E93,
              textAlign: "center",
            }}
          >
            {t("fileUpload.dragDrop.maxSizeInfo", {
              size: isImage
                ? (DEFAULT_FILE_SIZE_UPLOAD_LIMIT / (1024 * 1024)).toFixed(0)
                : (ROOMS_FILE_SIZE_UPLOAD_LIMIT / (1024 * 1024)).toFixed(0),
            })}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "column", md: "row" },
              alignItems: "center",
              justifyContent: "center",
              gap: { xs: "0.75rem", sm: "0.75rem", md: "0.5rem" },
              width: "100%",
            }}
          >
            <CommonButton
              onClick={() => {}}
              style={{
                borderRadius: "0.5rem",
                height: isMobile ? "2.5rem" : "2rem",
                minWidth: isMobileOrTablet ? "200px" : "auto",
                fontWeight: customMuiTheme.typography.fontWeightBold,
                fontSize: isMobile ? "0.875rem" : "1rem",
              }}
              purpleBorderVariant
            >
              {t("fileUpload.dragDrop.buttons.browse")}
            </CommonButton>
            {!disableImportDocCenter && (
              <>
                {!isMobileOrTablet && (
                  <Typography sx={{ color: Colors.LIGHT_GRAY_8A8E93 }}>{t("fileUpload.dragDrop.buttons.or")}</Typography>
                )}
                <Typography
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: ".2rem",
                    color: Colors.LAZO_PURPLE,
                    cursor: "pointer",
                    fontWeight: customMuiTheme.typography.fontWeightBold,
                    fontSize: { xs: "0.875rem", sm: "0.875rem", md: "1rem" },
                    textAlign: "center",
                    minHeight: isMobile ? "2.5rem" : "auto",
                    width: isMobileOrTablet ? "200px" : "auto",
                    ":hover": {
                      textDecoration: "underline",
                    },
                  }}
                  onClick={(ev) => {
                    ev.stopPropagation();
                    setOpenDocumentCenterImportModal(true);
                  }}
                >
                  {t("fileUpload.dragDrop.buttons.importFromDocCenter")}
                  <PurpleFilesIcon />
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </FileUploadContainer>
      <DataRoomDocumentCenterImportModal open={openDocumentCenterImportModal} setOpen={setOpenDocumentCenterImportModal} />
    </>
  );
}

const FileUploadContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  padding: theme.spacing(0.5),
  width: "100%",
  borderRadius: theme.spacing(2),
  backgroundColor: Colors.WHITE,
  boxShadow: "0 0 8px 0 rgba(0, 0, 0, 0.06)",
  alignItems: "center",
  "& .inside-container": {
    flexDirection: "column",
    borderRadius: theme.spacing(1.5),
    border: `1px dashed ${Colors.STROKE_DARK_GRAY}`,
    display: "flex",
    padding: theme.spacing(1, 1.5),
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.75),
    alignSelf: "stretch",
    width: "100%",
    transition: "all 0.2s ease-in",
    minHeight: theme.spacing(30),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(1.5, 2),
      gap: theme.spacing(1),
      minHeight: theme.spacing(35),
    },
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(4),
      gap: theme.spacing(1),
      minHeight: theme.spacing(40),
    },
    ":hover": {
      cursor: "pointer",
      backgroundColor: Colors.ACCENT_F1F3FF,
      border: `1px dashed ${Colors.BUTTON_HOVER_PURPLE}`,
    },
  },
}));

export default DataRoomFileDragDrop;
